import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  IconBell,
  IconBellRinging,
  IconCheck,
  IconHeart,
  IconMessage,
  IconUserPlus,
  IconShare,
  IconUsers,
  IconFolder
} from '@tabler/icons-react';
import { useNotifications } from '../../hooks/useNotifications';
import {
  selectFilteredNotifications,
  markAsRead as markAsReadAction
} from '../../store/notificationsSlice';
import notificationActionHandler from '../../services/notificationActionHandler';
import { timeAgo } from '../../../../utils/dateFormat';
import './NotificationBell.css';


const NotificationBell = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [animateCount, setAnimateCount] = useState(false);
  const dropdownRef = useRef(null);

  // Hooks
  const { unreadCount } = useNotifications();

  // Get recent notifications for dropdown preview (limit to 5)
  const recentNotifications = useSelector(selectFilteredNotifications).slice(0, 5);

  // Animate unread count when it changes
  useEffect(() => {
    if (unreadCount > 0) {
      setAnimateCount(true);
      const timer = setTimeout(() => setAnimateCount(false), 300);
      return () => clearTimeout(timer);
    }
  }, [unreadCount]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleNotificationClick = async (notification) => {
    try {
      // Use the action handler for proper notification handling
      const result = await notificationActionHandler.handleNotificationClick(notification, navigate);

      if (result.success) {
        setIsOpen(false);
      } else {
        console.error('Failed to handle notification click:', result.error);
      }
    } catch (error) {
      console.error('Error handling notification click:', error);
      // Fallback: just mark as read and close
      if (!notification.read) {
        dispatch(markAsReadAction([notification.id]));
      }
      setIsOpen(false);
    }
  };

  const handleMarkAllRead = () => {
    const unreadIds = recentNotifications
      .filter(n => !n.read)
      .map(n => n.id);

    if (unreadIds.length > 0) {
      dispatch(markAsReadAction(unreadIds));
    }
  };

  const handleViewAll = () => {
    navigate('/notifications');
    setIsOpen(false);
  };

  const getNotificationIcon = (type) => {
    const iconProps = { size: 16, className: 'notification-type-icon' };

    switch (type) {
      case 'like':
        return <IconHeart {...iconProps} className="notification-type-icon like" />;
      case 'comment':
      case 'reply':
        return <IconMessage {...iconProps} className="notification-type-icon comment" />;
      case 'follow':
      case 'follow_request':
      case 'follow_request_accepted':
        return <IconUserPlus {...iconProps} className="notification-type-icon follow" />;
      case 'share':
        return <IconShare {...iconProps} className="notification-type-icon share" />;
      case 'project_invite':
      case 'project_invite_accepted':
      case 'project_launched':
        return <IconUsers {...iconProps} className="notification-type-icon project" />;
      case 'solution_vote':
        return <IconFolder {...iconProps} className="notification-type-icon solution" />;
      case 'message':
        return <IconMessage {...iconProps} className="notification-type-icon comment" />;
      default:
        return <IconBell {...iconProps} className="notification-type-icon default" />;
    }
  };

  // Use shared timeAgo utility from utils/dateFormat.js
  const formatTimeAgo = (timestamp) => timeAgo(timestamp);

  // Generate notification title based on type (for server-created notifications without title)
  const getNotificationTitle = (notification) => {
    // If notification already has a title, use it
    if (notification.title) return notification.title;

    // Fallback titles based on type (actor name not available in Bell)
    switch (notification.type) {
      case 'message':
        return 'New message';
      case 'mention':
        return 'You were mentioned';
      case 'like':
        return 'Someone liked your post';
      case 'comment':
        return 'New comment on your post';
      case 'reply':
        return 'New reply to your comment';
      case 'follow':
        return 'New follower';
      case 'follow_request':
        return 'Follow request';
      case 'follow_request_accepted':
        return 'Follow request accepted';
      case 'share':
        return 'Your post was shared';
      case 'project_invite':
        return 'Project invitation';
      case 'project_invite_accepted':
        return 'Project invite accepted';
      case 'project_launched':
        return 'Project launched';
      case 'solution_vote':
        return 'Vote on your solution';
      default:
        return 'New notification';
    }
  };

  const getNotificationPreview = (notification) => {
    if (notification.previewText) {
      return notification.previewText;
    }

    // For message/mention notifications, use messageText from Cloud Function
    if ((notification.type === 'message' || notification.type === 'mention') && notification.messageText) {
      return `"${notification.messageText}"`;
    }

    if (notification.message && notification.message !== notification.title) {
      return notification.message;
    }

    return '';
  };

  return (
    <div className="notification-bell-container" ref={dropdownRef}>
      <button
        className={`notification-bell-button ${isOpen ? 'active' : ''}`}
        onClick={toggleDropdown}
        aria-label={`Notifications (${unreadCount} unread)`}
        title={`${unreadCount} unread notifications`}
      >
        {unreadCount > 0 ? (
          <IconBellRinging size={24} className="bell-icon ringing" />
        ) : (
          <IconBell size={24} className="bell-icon" />
        )}

        {unreadCount > 0 && (
          <span
            className={`unread-badge ${animateCount ? 'animate' : ''}`}
            data-count={unreadCount > 99 ? '99+' : String(unreadCount)}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Screen reader announcement for unread count */}
      <span
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}
      >
        {unreadCount > 0 ? `${unreadCount} unread notifications` : 'No unread notifications'}
      </span>

      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-dropdown-header">
            <h3>Notifications</h3>
            {unreadCount > 0 && (
              <button
                className="mark-all-read-btn"
                onClick={handleMarkAllRead}
                title="Mark all as read"
              >
                <IconCheck size={16} />
              </button>
            )}
          </div>

          <div className="notification-dropdown-content">
            {recentNotifications.length === 0 ? (
              <div className="no-notifications">
                <IconBell size={32} className="no-notifications-icon" />
                <p>No notifications yet</p>
              </div>
            ) : (
              <>
                {recentNotifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`notification-dropdown-item ${!notification.read ? 'unread' : ''}`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="notification-icon-wrapper">
                      {getNotificationIcon(notification.type)}
                    </div>

                    <div className="notification-content">
                      <div className="notification-title">
                        {getNotificationTitle(notification)}
                      </div>

                      {getNotificationPreview(notification) && (
                        <div className="notification-preview">
                          {getNotificationPreview(notification)}
                        </div>
                      )}

                      <div className="notification-time">
                        {formatTimeAgo(notification.createdAt)}
                      </div>
                    </div>

                    {!notification.read && (
                      <div className="unread-indicator" />
                    )}
                  </div>
                ))}

                <div className="notification-dropdown-footer">
                  <button
                    className="view-all-btn"
                    onClick={handleViewAll}
                  >
                    View all notifications
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
